import { useState } from "react";
import { motion } from "framer-motion";
import {
  Mic,
  MicOff,
  Plus,
  Users,
  MessageCircle,
  ArrowRight,
  Sparkles,
  Volume2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useVoiceDiscovery } from "@/hooks/useVoiceDiscovery";

interface VoiceDiscoveryLobbyProps {
  onJoinRoom: (roomId: string) => void;
}

const SUGGESTED_TOPICS = [
  "First Date Energy 💕",
  "Travel Stories ✈️",
  "Movie Night 🎬",
  "Music Lovers 🎵",
  "Late Night Talks 🌙",
  "Foodies Unite 🍕",
];

export const VoiceDiscoveryLobby = ({ onJoinRoom }: VoiceDiscoveryLobbyProps) => {
  const { rooms, loading, createRoom, joinRoom } = useVoiceDiscovery();
  const [showCreate, setShowCreate] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomTopic, setNewRoomTopic] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return;
    setIsCreating(true);
    try {
      const room = await createRoom(newRoomName, newRoomTopic);
      if (room) {
        await joinRoom(room.id);
        onJoinRoom(room.id);
        setShowCreate(false);
        setNewRoomName("");
        setNewRoomTopic("");
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinRoom = async (roomId: string) => {
    const success = await joinRoom(roomId);
    if (success) {
      onJoinRoom(roomId);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 px-4 py-4 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Volume2 className="w-6 h-6 text-primary" />
              Voice First
            </h1>
            <p className="text-sm text-muted-foreground">
              Match by personality, not photos
            </p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                Create Room
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Voice Room</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Room Name</label>
                  <Input
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                    placeholder="e.g., Coffee Chat ☕"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Topic (optional)</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {SUGGESTED_TOPICS.map((topic) => (
                      <Button
                        key={topic}
                        variant={newRoomTopic === topic ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNewRoomTopic(topic)}
                        className="text-xs"
                      >
                        {topic}
                      </Button>
                    ))}
                  </div>
                  <Input
                    value={newRoomTopic}
                    onChange={(e) => setNewRoomTopic(e.target.value)}
                    placeholder="Or type your own..."
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={handleCreateRoom}
                  disabled={!newRoomName.trim() || isCreating}
                >
                  {isCreating ? "Creating..." : "Create & Join"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* How it works */}
      <section className="px-4 py-6">
        <div className="p-4 rounded-2xl bg-gradient-to-br from-primary/10 via-pink-500/5 to-transparent border border-primary/20">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-foreground">How Voice First Works</h3>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center text-xs">
            <div className="space-y-1">
              <div className="w-10 h-10 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
                <Mic className="w-5 h-5 text-primary" />
              </div>
              <p className="text-muted-foreground">Join & talk</p>
            </div>
            <div className="space-y-1">
              <div className="w-10 h-10 mx-auto rounded-full bg-pink-500/20 flex items-center justify-center">
                <span className="text-lg">💕</span>
              </div>
              <p className="text-muted-foreground">Like their vibe</p>
            </div>
            <div className="space-y-1">
              <div className="w-10 h-10 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                <span className="text-lg">👀</span>
              </div>
              <p className="text-muted-foreground">Reveal photos</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-3">
            Photos stay hidden until mutual interest - focus on personality first!
          </p>
        </div>
      </section>

      {/* Active Rooms */}
      <section className="px-4 pb-24">
        <h2 className="text-lg font-semibold text-foreground mb-4">Active Rooms</h2>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : rooms.length === 0 ? (
          <div className="text-center py-12">
            <Volume2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground mb-4">No active rooms right now</p>
            <Button onClick={() => setShowCreate(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Be the first to create one!
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {rooms.map((room, index) => (
              <motion.div
                key={room.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group relative overflow-hidden rounded-2xl bg-card border border-border/50 hover:border-primary/30 transition-all"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{room.name}</h3>
                      {room.topic && (
                        <p className="text-sm text-muted-foreground">{room.topic}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-500 text-xs">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      Live
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span>{room.max_participants} max</span>
                    </div>
                    <Button
                      size="sm"
                      className="gap-2"
                      onClick={() => handleJoinRoom(room.id)}
                    >
                      Join
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
